import * as React from 'react';
import { connect } from 'react-redux';
import { Card } from '@origin-digital/layout';
import { FormikValues } from 'formik';

import { stepOne } from './content.json';
import { validation } from './validation';
import { StepOne, StepTwo } from './Steps';
import { RequestQuoteWizardState, RequestQuoteWizardProps } from './RequestQuoteWizard.types';

import Wizard from '../../components/Form/Wizard';
import ThankYou from '../../components/ThankYou';
import Banner from '../../components/Banner';
import { ResponsiveLayout } from '../../components/ResponsiveLayout';
import FormErrorSummary from '../../components/Form/Layout/FormErrorSummary';
import logger from '../../logging';
import analytics from '../../analytics';
import { isInitialValid } from '../../utils/formHelpers';
import { submitSolarForm } from '../../redux/thunks';
import { resetCustomerDetail } from '../../redux/actions/customer';
import { createCalculatorObjectFromQueryString } from '../../utils/queryHelpers';
import { CreateLeadInput } from '../../../__generated__/globalTypes.js';
import { StoreState } from '../../redux/store.types';
import { getUserToken } from '../../utils/cookieHelper';
import { config } from '../../utils/config';

export class RequestQuoteWizard extends React.Component<
  RequestQuoteWizardProps,
  RequestQuoteWizardState
> {
  static getDerivedStateFromProps(
    nextProps: RequestQuoteWizardProps
  ): Partial<RequestQuoteWizardState> {
    if (nextProps.productType === 'BATTERY') {
      return { validation: validation[1] };
    } else return { validation: validation[0] };
  }

  state = {
    validation: validation[0],
    showThankYouPage: false
  };

  urlParams = new URLSearchParams(window.location.search);

  async submit(values: FormikValues) {
    const { productType, propertyType, roofType, postcode, submitSolarForm } = this.props;

    try {
      const { data } = await submitSolarForm(this.mapFormValuesToCreateLeadInput(values));

      if (data) {
        const r = /[_\s]/g;
        const product = productType.toLowerCase().replace(r, '-');
        const property =
          productType !== 'BATTERY' ? propertyType!.toLowerCase().replace(r, '-') : undefined;
        const roof =
          productType !== 'BATTERY' ? roofType!.toLowerCase().replace(r, '-') : undefined;

        analytics.submitSuccessful({
          leadId: data.createLead.lead.leadId,
          solarProduct: product,
          propertyType: property,
          roofType: roof,
          postcode: postcode
        });

        this.showThankYouPage();
      }
    } catch (error) {
      logger.error('GraphQL Error', error);
      return;
    }
  }

  mapFormValuesToCreateLeadInput(values: FormikValues): CreateLeadInput {
    const normalizedCurrency = parseFloat(
      values.averageQuarterlyBillCost.substring(1, values.averageQuarterlyBillCost.length)
    );

    return {
      productType: values.productType,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      address: {
        unit: values.unit,
        streetNumber: values.streetNumber,
        streetName: values.streetName,
        streetType: values.streetType,
        suburb: values.suburb,
        state: values.state,
        postcode: values.postcode
      },
      comment: this.urlParams.has('whisper') ? this.urlParams.get('whisper') : '',
      propertyType: values.propertyType,
      roofType: values.roofType,
      averageQuarterlyBillCost: normalizedCurrency,
      usagePercentDuringDay: parseFloat(values.usagePercentDuringDay),
      propertyStorey: values.propertyStorey,
      powerConnectionPhaseType: values.powerConnectionPhaseType,
      roofPitch: values.roofPitch,
      allowMarketingCommunications: values.allowMarketingCommunications,
      calculatorValues:
        createCalculatorObjectFromQueryString(
          values.postcode,
          values.usagePercentDuringDay,
          normalizedCurrency
        ) || {}
    };
  }

  showThankYouPage(): void {
    this.setState({
      showThankYouPage: true
    });
  }

  componentDidMount(): void {
    const { setVisibility } = this.props;
    // if user is logged in redirect to auth quote page
    if (getUserToken()) {
      this.redirecToAuthQuote();
    } else {
      setVisibility(false);
    }
  }

  redirecToAuthQuote(): void {
    window.location.href =
      config.ENV === 'local' ? '/auth-quote' : '/my-account/solar-request-quote';
  }

  render(): JSX.Element | null {
    const { validation, showThankYouPage } = this.state;
    const { validating, asyncSubmitting, initialValues } = this.props;
    console.log('this.urlParams.get', this.urlParams.get('whisper'));

    return (
      <React.Fragment>
        <Banner
          title={'Get a solar quote'}
          subtitle={'For new solar panels, inverters and batteries'}
        />
        <ResponsiveLayout min={['SM', 'MD']}>
          <Card>
            <FormErrorSummary marginBottom={8} />
            {showThankYouPage ? (
              <ThankYou data={stepOne} />
            ) : (
              <Wizard
                initialValues={initialValues}
                validation={validation}
                isSubmitting={asyncSubmitting}
                enableReinitialize={false}
                isInitialValid={isInitialValid}
                validating={validating}
                buttonLabels={stepOne.buttons}
                onSubmit={this.submit.bind(this)}
              >
                <Wizard.Step>
                  <StepOne isInitialValid={isInitialValid} />
                </Wizard.Step>
                <Wizard.Step>
                  <StepTwo isInitialValid={isInitialValid} />
                </Wizard.Step>
              </Wizard>
            )}
          </Card>
        </ResponsiveLayout>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: StoreState) => {
  return {
    initialValues: {
      ...state.customer
    },
    productType: state.customer.productType,
    propertyType: state.customer.propertyType,
    roofType: state.customer.roofType,
    postcode: state.customer.postcode,
    validating: state.request.validating,
    asyncSubmitting: state.request.submitting
  };
};

export default connect(
  mapStateToProps,
  { submitSolarForm, resetCustomerDetail }
)(RequestQuoteWizard);
